<template>
  <div class="profilesSwitch">
    <h3>Switch funds</h3>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <div>
        <div class="mt-4 mb-2">Switch sale of: {{ fundToSwitch }}</div>
      </div>
      <div>
        <DatePicker v-model="switchDate" label="Switch date" :rules="[(v) => !!v || 'Required.']" />
      </div>
      <div>
        <v-select
          v-model="switchType"
          :items="switchTypeList"
          label="Switch type"
          outlined
          dense
        ></v-select>
        <v-text-field
          v-model="switchAmount"
          label="Switch amount"
          type="number"
          :step="stepAmount"
          :min="minAmount"
          :max="maxAmount"
          :rules="switchAmountRules"
          outlined
          dense
        ></v-text-field>
      </div>
      <div class="mb-4">Select Fund to purchase</div>
      <div>
        <v-select
          v-model="switchManager"
          :items="switchManagerList"
          item-value="ManagerId"
          item-text="ManagerName"
          label="Manager"
          :rules="[(v) => !!v || 'Required.']"
          outlined
          dense
        ></v-select>
      </div>
      <div>
        <v-select
          v-model="switchFundId"
          :items="switchFundList"
          item-value="PortfolioId"
          item-text="PortfolioName"
          label="Fund"
          :rules="[(v) => !!v || 'Required.']"
          outlined
          dense
        ></v-select>
      </div>
    </v-form>
    <div class="btnsContainer">
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" @click="onClose" class="ml-4">Close</v-btn>
      <v-btn depressed color="primary" class="ml-4" :disabled="!isFormValid" @click="onSwitch"
        >Switch</v-btn
      >
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/formControls/DatePicker.vue';
import Utils from '@/utils';

export default {
  name: 'ProfilesSwitch',
  components: { DatePicker },
  props: { fundToSwitch: String, unitsHeld: Number, memberId: Number, sellProfile: Object },
  data() {
    return {
      isFormValid: false,
      loading: false,
      switchDate: null,
      switchType: null,
      switchTypeList: ['%', 'Units', 'Value'],
      switchAmount: null,
      switchManager: null,
      switchFundId: null,
      switchManagerList: [],
      switchFundList: [],
    };
  },

  computed: {
    switchFund() {
      if (this.switchFundId)
        return this.switchFundList.find((fund) => fund.PortfolioId === this.switchFundId);
      return {};
    },

    maxUnits() {
      return Math.round(this.unitsHeld * 100000) / 100000;
    },

    minAmount() {
      switch (this.switchType) {
        case '%':
          return 1;
        case 'Units':
          return 0.00001;
        case 'Value':
          return 0.01;
        default:
          return 1;
      }
    },

    maxAmount() {
      switch (this.switchType) {
        case '%':
          return 100;
        case 'Units':
          return this.maxUnits;
        case 'Value':
          return null;
        default:
          return null;
      }
    },

    stepAmount() {
      return this.minAmount;
    },

    switchAmountRules() {
      const generalRules = [(v) => !!v || 'Required.'];

      if (this.switchType === '%')
        return [
          (v) =>
            (!isNaN(Number(v)) && v > 0 && v <= 100) ||
            'Amount should be a number between 1 and 100.',
          ...generalRules,
        ];

      if (this.switchType === 'Units')
        return [
          (v) =>
            (!isNaN(Number(v)) && v > 0 && v <= this.maxUnits) ||
            `Amount should be a number between 0.00001 and ${this.maxUnits}.`,
          ...generalRules,
        ];

      if (this.switchType === 'Value')
        return [
          (v) => (!isNaN(Number(v)) && v > 0) || 'Amount should be a positive number.',
          ...generalRules,
        ];

      return generalRules;
    },
  },

  watch: {
    switchManager() {
      this.getFundList();
    },
  },

  created() {
    this.getManagerList();
  },

  methods: {
    onClose() {
      this.$emit('closeSwitch');
    },

    onSwitch() {
      this.isFormValid = this.$refs.form.validate();
      if (this.isFormValid) {
        const newSwitch = {
          SwitchId: 0,
          MembershipId: this.memberId,
          SellProfileId: this.sellProfile.ProfileId,
          SellFundId: this.sellProfile.FundId,
          SwitchType: this.switchType,
          SwitchAmount: Number(this.switchAmount),
          SwitchDate: this.switchDate,
          CashHeld: 0,
          UnitsSold: 0,
          Status: '',
          Source: '',
          BuyProfileId: 0,
          BuyFundId: this.switchFundId,
          BuyFundName: this.switchFund.PortfolioName,
          BuyManagerName: this.switchManagerList.find((m) => m.ManagerId === this.switchManager)
            .ManagerName,
          ContributionType: this.sellProfile.ContributionType,
          InvestmentType: this.sellProfile.InvestmentType,
          PortfolioName: this.switchFund.PortfolioName,
          ManagerName: this.sellProfile.ManagerName,
          SwitchDateString: Utils.getDateStr(new Date(this.switchDate)),
        };
        console.log(newSwitch);
        this.addSwitch(newSwitch);
        this.onClose();
      }
    },

    async getManagerList() {
      this.loading = true;
      try {
        const response = await this.$api.get('/Funds/GetManagerList');

        this.switchManagerList = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async getFundList() {
      this.loading = true;
      try {
        const response = await this.$api.get('/Funds/GetFundList?managerId=' + this.switchManager);

        this.switchFundList = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async addSwitch(newSwitch) {
      this.loading = true;
      try {
        await this.$api.post('/switch/UpdateSwitch', newSwitch);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'New switch is created',
        });
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.btnsContainer {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: white;
  padding: 10px;
  display: flex;
  justify-content: space-between;
}
.btnsContainer > div {
  display: inline-block;
}
</style>
